// Os três documentos do Simulador MEI: o CCMEI, o DAS e o recibo da DASN-SIMEI.
//
// Puros: entram os dados, sai uma string de HTML. Quem abre a janela e chama o
// `print()` é a tela, porque isso é IO. A divisão é a de `resumoHonorarios.ts`
// (puro) com `CalculadoraHonorarios.tsx` (janela), pelo mesmo motivo: assim os
// documentos têm prova sem precisar de navegador.
//
// ---------------------------------------------------------------------------
// Por que cada valor passa por `escapar` — e por que aqui isso é mais grave
// ---------------------------------------------------------------------------
// O simulador de origem montava os três documentos com interpolação CRUA e os
// escrevia com `document.write` numa janela mesma-origem:
//
//     <tr><td>Nome do empresário</td><td>${form.nome}</td></tr>
//
// `form.nome` é o que o aluno digitou. Um `<script>` ali roda com a sessão do
// hub — e, ao contrário do resumo de honorários (onde quem digita é o cliente
// do aluno), aqui quem digita é o próprio aluno, então o alcance é a conta dele
// mesmo. Auto-XSS é o degrau mais baixo da escala, e é exatamente por isso que
// ele passa em revisão: ninguém se preocupa. Mas o hub não tem uma única
// ocorrência de `dangerouslySetInnerHTML`, e a marcação própria do `TextoRico`
// existe justamente para não abrir a primeira exceção. Um documento educacional
// não é motivo para abrir.
//
// ---------------------------------------------------------------------------
// Nada de `new Date()` nem `Math.random()` aqui dentro
// ---------------------------------------------------------------------------
// O original chamava os dois dentro do HTML — data de emissão, código de
// autenticação, número do DAS, linha digitável. Função que sorteia não tem
// prova: cada chamada devolve outra coisa. Então tudo isso entra como
// PARÂMETRO, já formatado, e quem sorteia é a tela. É a mesma decisão do
// `Cabecalho.data` do resumo de honorários.
//
// ---------------------------------------------------------------------------
// A marca d'água não é enfeite
// ---------------------------------------------------------------------------
// Os três documentos são visualmente fiéis aos oficiais — é isso que faz o
// aluno reconhecer o documento quando o vê na vida real, e é o valor pedagógico
// da ferramenta. Fidelidade visual num documento fiscal pede o contrário na
// mesma medida: "DOCUMENTO FICTÍCIO" atravessado na página, o selo de SIMULAÇÃO
// no cabeçalho e o rodapé dizendo que não tem validade jurídica. Os três ficam,
// e não são configuráveis.

function escapar(v: string | null | undefined): string {
  return String(v ?? "")
    .split("&")
    .join("&amp;")
    .split("<")
    .join("&lt;")
    .split(">")
    .join("&gt;")
    .split('"')
    .join("&quot;")
    .split("'")
    .join("&#39;");
}

function reais(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Só o número, para as colunas de tabela dos documentos oficiais. */
function numero(v: number): string {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const AVISO_RODAPE =
  "Este documento é uma SIMULAÇÃO EDUCACIONAL da Contabilidade Facilitada. " +
  "Não possui validade jurídica, não foi emitido por órgão público e não substitui " +
  "o documento oficial.";

const MARCA_DAGUA = '<div class="marca">DOCUMENTO FICTÍCIO</div>';

// O bloco de estilo comum: marca d'água, botões que desaparecem na impressão e
// a tipografia dos documentos do governo.
const ESTILO_BASE = `
  * { box-sizing: border-box }
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 24px;
         background: #eee; color: #222; font-size: 12px }
  .acoes { max-width: 840px; margin: 0 auto 12px; text-align: right }
  .acoes button { background: #102850; color: #fff; border: 0; padding: 8px 14px;
                  border-radius: 4px; font-weight: bold; cursor: pointer; margin-left: 6px }
  .acoes button.secundario { background: #727376 }
  .folha { max-width: 840px; margin: 0 auto 14px; background: #fff; padding: 32px 40px;
           position: relative; overflow: hidden }
  .folha > * { position: relative; z-index: 1 }
  .marca { position: absolute; top: 50%; left: 50%; z-index: 0;
           transform: translate(-50%, -50%) rotate(-25deg); font-size: 92px;
           color: rgba(200,30,30,.10); font-weight: 900; letter-spacing: 6px;
           pointer-events: none; white-space: nowrap }
  .selo-simulacao { display: inline-block; background: #c53030; color: #fff; font-size: 10px;
                    padding: 2px 8px; border-radius: 3px; font-weight: bold;
                    letter-spacing: .5px }
  .rodape-aviso { margin-top: 22px; padding-top: 8px; border-top: 1px solid #ddd;
                  font-size: 10px; color: #666; text-align: center; line-height: 1.5 }
  @media print { .acoes { display: none } body { background: #fff; padding: 0 } }
`;

const ACOES = `
<div class="acoes">
  <button onclick="window.print()">Imprimir / Salvar PDF</button>
  <button class="secundario" onclick="window.close()">Fechar</button>
</div>`;

// ===========================================================================
// 1. CCMEI — Certificado da Condição de Microempreendedor Individual
// ===========================================================================

export type SecundariaCcmei = { cnae: string; descricao: string };

export type DadosCcmei = {
  cnpj: string;
  nomeEmpresarial: string;
  nomeEmpresario: string;
  cpf: string;
  /** Já formatada — a função é pura. */
  dataAbertura: string;
  dataEmissao: string;
  capitalSocial: string;
  enderecoComercial: string;
  ocupacaoPrincipal: string;
  cnaePrincipal: string;
  descricaoCnaePrincipal: string;
  formasAtuacao: string[];
  secundarias: SecundariaCcmei[];
  /** Sorteado pela tela, não aqui. */
  codigoAutenticacao: string;
};

export function htmlDoCcmei(d: DadosCcmei): string {
  const secundarias =
    d.secundarias.length > 0
      ? d.secundarias
          .map((s) => `<li>${escapar(s.cnae)} — ${escapar(s.descricao)}</li>`)
          .join("")
      : "<li>Nenhuma</li>";

  const formas =
    d.formasAtuacao.length > 0
      ? d.formasAtuacao.map((f) => escapar(f)).join(", ")
      : "&mdash;";

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>CCMEI (simulação) — ${escapar(d.cnpj)}</title>
<style>${ESTILO_BASE}
  .cabeca { display: flex; align-items: center; gap: 16px; border-bottom: 3px solid #102850;
            padding-bottom: 12px; margin-bottom: 18px }
  .brasao { width: 54px; height: 54px; border-radius: 50%; background: #102850; color: #fff;
            display: flex; align-items: center; justify-content: center; font-weight: bold;
            font-size: 11px; text-align: center; line-height: 1.1; flex-shrink: 0 }
  h1 { color: #102850; font-size: 17px; margin: 0; line-height: 1.25 }
  .sub { color: #555; font-size: 11px; margin-top: 4px }
  h2 { color: #102850; font-size: 12px; margin: 18px 0 4px; padding-bottom: 3px;
       border-bottom: 1px solid #ccd; text-transform: uppercase; letter-spacing: .04em }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px }
  td { padding: 4px 6px; vertical-align: top; border-bottom: 1px solid #eee }
  td.k { color: #555; width: 38% }
  ul { margin: 4px 0 0 18px; padding: 0; font-size: 12px }
  p.end { font-size: 12px; margin: 4px 0 }
  p.termo { font-size: 11px; line-height: 1.55; margin: 4px 0 0 }
</style></head>
<body>
${ACOES}
<div class="folha">
  ${MARCA_DAGUA}
  <div class="cabeca">
    <div class="brasao">GOV<br>BR</div>
    <div>
      <h1>Certificado da Condição de Microempreendedor Individual</h1>
      <div class="sub">CCMEI &middot; Emitido em ${escapar(d.dataEmissao)} &middot; <span class="selo-simulacao">SIMULAÇÃO</span></div>
    </div>
  </div>

  <h2>Empresa</h2>
  <table>
    <tr><td class="k">Nome empresarial</td><td><b>${escapar(d.nomeEmpresarial)}</b></td></tr>
    <tr><td class="k">CNPJ</td><td><b>${escapar(d.cnpj)}</b></td></tr>
    <tr><td class="k">Data de início de atividades</td><td>${escapar(d.dataAbertura)}</td></tr>
    <tr><td class="k">Situação cadastral</td><td>ATIVA</td></tr>
    <tr><td class="k">Natureza jurídica</td><td>213-5 — Empresário (Individual)</td></tr>
    <tr><td class="k">Capital social</td><td>R$ ${escapar(d.capitalSocial || "0,00")}</td></tr>
    <tr><td class="k">Porte</td><td>Microempresa (ME)</td></tr>
  </table>

  <h2>Empresário</h2>
  <table>
    <tr><td class="k">Nome do empresário</td><td>${escapar(d.nomeEmpresario)}</td></tr>
    <tr><td class="k">CPF</td><td>${escapar(d.cpf)}</td></tr>
  </table>

  <h2>Endereço comercial</h2>
  <p class="end">${escapar(d.enderecoComercial) || "&mdash;"}</p>

  <h2>Atividades</h2>
  <table>
    <tr><td class="k">Ocupação principal</td><td>${escapar(d.ocupacaoPrincipal) || "&mdash;"}</td></tr>
    <tr><td class="k">CNAE principal</td><td>${escapar(d.cnaePrincipal)}${
      d.descricaoCnaePrincipal ? ` — ${escapar(d.descricaoCnaePrincipal)}` : ""
    }</td></tr>
    <tr><td class="k">Forma de atuação</td><td>${formas}</td></tr>
  </table>
  <p class="end"><b>CNAEs secundários:</b></p>
  <ul>${secundarias}</ul>

  <h2>Termo de ciência e responsabilidade com efeito de dispensa de alvará e licença de funcionamento</h2>
  <p class="termo">O empresário declara, sob as penas da lei, que conhece e atende os requisitos
  legais exigidos pelo Estado e pela Prefeitura do Município para a dispensa da emissão do Alvará
  e Licença de Funcionamento, compreendidos os aspectos sanitários, ambientais, tributários, de
  segurança pública, uso e ocupação do solo, atividades domiciliares e restrições ao uso de
  espaços públicos. Declara, ainda, ter ciência de que dados empresariais são públicos, nos termos
  da Lei nº 8.934/1994.</p>

  <div class="rodape-aviso">
    ${AVISO_RODAPE}<br>
    O CCMEI oficial é emitido em gov.br/empresas-e-negocios.<br>
    Código de autenticação fictício: ${escapar(d.codigoAutenticacao)}
  </div>
</div>
</body></html>`;
}

// ===========================================================================
// 2. DAS — Documento de Arrecadação do Simples Nacional
// ===========================================================================

export type ComposicaoDas = { inss: number; icms: number; iss: number; total: number };

export type PaginaDas = {
  /** "Março/2026" — o período de apuração como o PGMEI escreve. */
  periodoApuracao: string;
  /** "03/2026" — o mesmo período no formato curto da composição. */
  periodoCurto: string;
  vencimento: string;
  numeroDocumento: string;
  composicao: ComposicaoDas;
};

export type DadosDas = {
  cnpj: string;
  razaoSocial: string;
  cpf: string;
  municipio: string;
  uf: string;
  /** A data que o aluno escolheu para pagar. */
  dataPagamento: string;
  dataEmissao: string;
  linhaDigitavel: string;
  paginas: PaginaDas[];
};

export function htmlDoDas(d: DadosDas): string {
  const folhas = d.paginas
    .map((p) => {
      const linhas: string[] = [];
      const local = `${escapar(d.municipio.toUpperCase())} (${escapar(d.uf)}) - ${escapar(p.periodoCurto)}`;
      // Só entra na composição o tributo que o MEI de fato deve. Imprimir uma
      // linha de R$ 0,00 fez o aluno perguntar por que paga ISS zero — e a
      // resposta é que ele não paga ISS nenhum.
      if (p.composicao.inss > 0) {
        linhas.push(
          `<tr><td class="cod">0151</td><td>INSS - SIMPLES NACIONAL - MEI<br><span class="sub">${escapar(p.periodoCurto)}</span></td>` +
            `<td class="r">${numero(p.composicao.inss)}</td><td class="r"></td><td class="r"></td><td class="r">${numero(p.composicao.inss)}</td></tr>`,
        );
      }
      if (p.composicao.icms > 0) {
        linhas.push(
          `<tr><td class="cod">0181</td><td>ICMS - SIMPLES NACIONAL - MEI<br><span class="sub">${local}</span></td>` +
            `<td class="r">${numero(p.composicao.icms)}</td><td class="r"></td><td class="r"></td><td class="r">${numero(p.composicao.icms)}</td></tr>`,
        );
      }
      if (p.composicao.iss > 0) {
        linhas.push(
          `<tr><td class="cod">0125</td><td>ISS - SIMPLES NACIONAL - MEI<br><span class="sub">${local}</span></td>` +
            `<td class="r">${numero(p.composicao.iss)}</td><td class="r"></td><td class="r"></td><td class="r">${numero(p.composicao.iss)}</td></tr>`,
        );
      }

      return `
<div class="folha">
  ${MARCA_DAGUA}
  <div class="cabeca">
    <div class="logo">
      <div class="circulo"><span>S</span></div>
      <div><div class="marca-nome">SIMPLES</div><div class="marca-sub">N A C I O N A L</div></div>
    </div>
    <div class="titulo">Documento de Arrecadação<br>do Simples Nacional<br><span class="selo-simulacao">SIMULAÇÃO</span></div>
  </div>

  <div class="linha">
    <fieldset class="cx cnpj"><legend>CNPJ</legend><div class="v b">${escapar(d.cnpj)}</div></fieldset>
    <fieldset class="cx razao"><legend>Razão Social</legend><div class="v b">${escapar(d.razaoSocial)}</div></fieldset>
  </div>
  <div class="linha">
    <fieldset class="cx um"><legend>Período de Apuração</legend><div class="v b c">${escapar(p.periodoApuracao)}</div></fieldset>
    <fieldset class="cx um"><legend>Data de Vencimento</legend><div class="v b c">${escapar(p.vencimento)}</div></fieldset>
    <fieldset class="cx num"><legend>Número do Documento</legend><div class="v b">${escapar(p.numeroDocumento)}</div></fieldset>
    <div class="verde"><div class="verde-rot">Pagar este documento até</div><div class="verde-val">${escapar(d.dataPagamento)}</div></div>
  </div>
  <div class="linha">
    <fieldset class="cx um"><legend>Observações</legend>
      <div class="v obs">${d.cpf ? `<strong>CPF: ${escapar(d.cpf)}</strong><br>` : ""}<strong>Tributos (R$): INSS ${numero(p.composicao.inss)} &nbsp; ICMS ${numero(p.composicao.icms)} &nbsp; ISS ${numero(p.composicao.iss)}</strong><br><strong>PGMEI — versão educacional</strong></div>
    </fieldset>
    <div class="verde"><div class="verde-rot">Valor Total do Documento</div><div class="verde-val">${numero(p.composicao.total)}</div></div>
  </div>

  <div class="comp-cab">Composição do Documento de Arrecadação</div>
  <table class="comp">
    <thead><tr><th>Código</th><th>Denominação</th><th class="r">Principal</th><th class="r">Multa</th><th class="r">Juros</th><th class="r">Total</th></tr></thead>
    <tbody>
      ${linhas.join("")}
      <tr class="totais"><td></td><td><strong>Totais</strong></td><td class="r"><strong>${numero(p.composicao.total)}</strong></td><td class="r"></td><td class="r"></td><td class="r"><strong>${numero(p.composicao.total)}</strong></td></tr>
    </tbody>
  </table>

  <div class="pe"><span>Emitido em ${escapar(d.dataEmissao)}</span><span>Página 1 / 1</span></div>
  <div class="pe2"><span class="ld">${escapar(d.linhaDigitavel)}</span><span>SEM AUTENTICAÇÃO — DOCUMENTO SIMULADO</span></div>

  <div class="corte"></div>
  <div class="recibo">
    <div class="recibo-tit">Documento de Arrecadação do Simples Nacional</div>
    <div class="ld-r">${escapar(d.linhaDigitavel)}</div>
    <div class="recibo-linha">
      <div class="cbarra">código de barras não emitido — documento simulado</div>
      <table class="ri">
        <tr><td>CNPJ:</td><td class="r">${escapar(d.cnpj)}</td></tr>
        <tr><td>Número:</td><td class="r">${escapar(p.numeroDocumento)}</td></tr>
        <tr><td>Pagar até:</td><td class="r">${escapar(p.vencimento)}</td></tr>
        <tr><td>Valor:</td><td class="r">${numero(p.composicao.total)}</td></tr>
      </table>
    </div>
  </div>

  <div class="rodape-aviso">${AVISO_RODAPE} Este DAS não pode ser pago: não há código de barras nem registro bancário.</div>
</div>`;
    })
    .join('<div style="page-break-after:always"></div>');

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>DAS (simulação) — ${escapar(d.cnpj)}</title>
<style>${ESTILO_BASE}
  .cabeca { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px }
  .logo { display: flex; align-items: center; gap: 10px }
  .circulo { width: 60px; height: 60px; border-radius: 50%;
             background: radial-gradient(circle at 30% 30%, #ffe600 0%, #ffe600 28%, #1d8d3a 75%);
             display: flex; align-items: center; justify-content: center;
             box-shadow: inset 0 0 0 4px #fff; flex-shrink: 0 }
  .circulo span { color: #fff; font-style: italic; font-weight: 900; font-size: 42px; line-height: 1;
                  text-shadow: 1px 1px 0 #1d8d3a }
  .marca-nome { font-size: 28px; font-weight: 900; color: #1d8d3a; letter-spacing: 1px; line-height: 1 }
  .marca-sub { font-size: 10px; color: #1d8d3a; letter-spacing: 3px; margin-top: 2px }
  .titulo { text-align: right; color: #0a2c66; font-size: 20px; font-weight: bold; line-height: 1.2 }
  .linha { display: flex; gap: 6px; margin-top: 6px; align-items: stretch }
  fieldset.cx { border: 1px solid #0a2c66; border-radius: 8px; padding: 2px 10px 6px; margin: 0;
                min-height: 48px; display: flex; flex-direction: column; justify-content: center }
  fieldset.cx legend { font-size: 10px; color: #0a2c66; padding: 0 4px; margin-left: 6px }
  .cx.cnpj { flex: 0 0 38% } .cx.razao { flex: 1 } .cx.um { flex: 1 } .cx.num { flex: 1.4 }
  .v { font-size: 13px } .v.b { font-weight: bold } .v.c { text-align: center }
  .obs { font-weight: bold; font-size: 12px; line-height: 1.45 }
  .verde { background: #5cb338; color: #fff; border-radius: 8px; padding: 6px 14px; flex: 0 0 180px;
           display: flex; flex-direction: column; justify-content: center; text-align: right;
           min-height: 48px }
  .verde-rot { font-size: 10px } .verde-val { font-size: 20px; font-weight: bold; line-height: 1.1 }
  .comp-cab { background: #0a2c66; color: #fff; padding: 5px 10px; font-weight: bold;
              margin-top: 14px; font-size: 12px; border-radius: 2px }
  table.comp { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px }
  table.comp th { color: #0a2c66; text-align: left; padding: 4px 8px; font-weight: bold }
  table.comp td { padding: 4px 8px; vertical-align: top }
  table.comp .r, table.comp th.r { text-align: right }
  table.comp td.cod { font-family: "Courier New", monospace }
  table.comp .sub { color: #666; font-size: 10px }
  table.comp tr.totais td { border-top: 2px solid #0a2c66; background: #f5f5f5 }
  .pe, .pe2 { display: flex; justify-content: space-between; font-size: 10px; color: #666;
              margin-top: 14px; padding-top: 6px; border-top: 1px dashed #999 }
  .pe2 { border-top: 0; margin-top: 4px }
  .ld { font-family: "Courier New", monospace; font-weight: bold; color: #222 }
  .corte { border-top: 2px dashed #999; margin: 22px 0 14px }
  .recibo-tit { color: #0a2c66; font-weight: bold; font-size: 12px; margin-bottom: 6px }
  .ld-r { font-family: "Courier New", monospace; font-weight: bold; font-size: 12px; margin-bottom: 10px }
  .recibo-linha { display: flex; gap: 16px; align-items: center }
  .cbarra { flex: 1; border: 1px dashed #999; border-radius: 4px; padding: 18px 10px;
            text-align: center; font-size: 10px; color: #888; text-transform: uppercase;
            letter-spacing: .08em }
  table.ri { flex: 0 0 260px; border-collapse: collapse; font-size: 11px }
  table.ri td { padding: 3px 6px; border-bottom: 1px solid #eee }
  table.ri td.r { text-align: right; font-weight: bold }
</style></head>
<body>
${ACOES}
${folhas}
</body></html>`;
}

// ===========================================================================
// 3. Recibo da DASN-SIMEI
// ===========================================================================

export type LinhaApuracao = {
  periodo: string;
  inss: number;
  icms: number;
  iss: number;
  total: number;
};

export type DadosReciboDasn = {
  cnpj: string;
  nomeEmpresarial: string;
  ano: number;
  tipoDeclaracao: string;
  numeroRecibo: string;
  dataTransmissao: string;
  receitaComercio: number;
  receitaServicos: number;
  receitaTotal: number;
  teveEmpregado: boolean;
  linhas: LinhaApuracao[];
};

export function htmlDoReciboDasn(d: DadosReciboDasn): string {
  const corpo = d.linhas
    .map(
      (l) =>
        `<tr><td>${escapar(l.periodo)}</td><td>Não</td>` +
        `<td class="r">${numero(l.inss)}</td><td class="r">${numero(l.icms)}</td>` +
        `<td class="r">${numero(l.iss)}</td><td class="r">${numero(l.total)}</td>` +
        `<td class="r">&mdash;</td></tr>`,
    )
    .join("");

  const soma = (campo: keyof LinhaApuracao) =>
    Math.round(d.linhas.reduce((s, l) => s + Math.round(Number(l[campo]) * 100), 0)) / 100;

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>Recibo DASN-SIMEI (simulação) — ${escapar(String(d.ano))}</title>
<style>${ESTILO_BASE}
  .cabeca { display: flex; justify-content: space-between; align-items: center;
            border-bottom: 3px solid #102850; padding-bottom: 12px; margin-bottom: 16px }
  .marca-nome { color: #102850; font-weight: 900; font-size: 21px; letter-spacing: 1px }
  .marca-nome small { color: #ff9933; font-weight: 900; display: block; font-size: 10px;
                      letter-spacing: 3px }
  .titulo { color: #102850; font-weight: bold; font-size: 15px; text-align: right; line-height: 1.25 }
  h3.sec { color: #102850; font-size: 12px; margin: 18px 0 6px; padding-bottom: 4px;
           border-bottom: 1px solid #102850; text-transform: uppercase; letter-spacing: .5px }
  fieldset { border: 1px solid #102850; border-radius: 6px; padding: 4px 10px 8px; margin: 6px 0 }
  fieldset legend { font-size: 10px; color: #102850; padding: 0 4px }
  .linha { display: flex; gap: 8px } .linha > * { flex: 1 }
  .v { font-size: 13px; font-weight: bold }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px }
  th { background: #102850; color: #fff; padding: 6px 8px; text-align: left }
  th.r, td.r { text-align: right }
  td { padding: 5px 8px; border-bottom: 1px solid #eee }
  tr.tot td { background: #f5f5f5; font-weight: bold; border-top: 2px solid #102850 }
</style></head>
<body>
${ACOES}
<div class="folha">
  ${MARCA_DAGUA}
  <div class="cabeca">
    <div class="marca-nome">DASN-SIMEI<small>DECLARAÇÃO ANUAL</small></div>
    <div class="titulo">Recibo de Entrega<br>Exercício ${escapar(String(d.ano))}<br><span class="selo-simulacao">SIMULAÇÃO</span></div>
  </div>

  <h3 class="sec">1. Informações do contribuinte e da declaração</h3>
  <div class="linha">
    <fieldset><legend>CNPJ</legend><div class="v">${escapar(d.cnpj)}</div></fieldset>
    <fieldset style="flex:2"><legend>Nome Empresarial</legend><div class="v">${escapar(d.nomeEmpresarial)}</div></fieldset>
  </div>
  <div class="linha">
    <fieldset><legend>Ano-calendário</legend><div class="v">${escapar(String(d.ano))}</div></fieldset>
    <fieldset><legend>Tipo de Declaração</legend><div class="v">${escapar(d.tipoDeclaracao)}</div></fieldset>
  </div>
  <div class="linha">
    <fieldset><legend>Número do Recibo</legend><div class="v">${escapar(d.numeroRecibo)}</div></fieldset>
    <fieldset><legend>Data de Transmissão</legend><div class="v">${escapar(d.dataTransmissao)}</div></fieldset>
  </div>

  <h3 class="sec">2. Resumo da declaração</h3>
  <table>
    <thead><tr><th>PA</th><th>Benefício INSS</th><th class="r">INSS</th><th class="r">ICMS</th><th class="r">ISS</th><th class="r">Valor Apurado</th><th class="r">Valor Pago</th></tr></thead>
    <tbody>${corpo}
      <tr class="tot"><td>Total</td><td></td>
        <td class="r">${numero(soma("inss"))}</td>
        <td class="r">${numero(soma("icms"))}</td>
        <td class="r">${numero(soma("iss"))}</td>
        <td class="r">${numero(soma("total"))}</td>
        <td></td></tr>
    </tbody>
  </table>

  <h3 class="sec">3. Informações socioeconômicas e fiscais</h3>
  <div class="linha">
    <fieldset><legend>Receita Bruta — Comércio/Indústria</legend><div class="v">${escapar(reais(d.receitaComercio))}</div></fieldset>
    <fieldset><legend>Receita Bruta — Serviços</legend><div class="v">${escapar(reais(d.receitaServicos))}</div></fieldset>
  </div>
  <div class="linha">
    <fieldset><legend>Receita Bruta Total</legend><div class="v">${escapar(reais(d.receitaTotal))}</div></fieldset>
    <fieldset><legend>Possuiu empregado no período</legend><div class="v">${d.teveEmpregado ? "Sim" : "Não"}</div></fieldset>
  </div>

  <div class="rodape-aviso">${AVISO_RODAPE} Nenhuma declaração foi transmitida à Receita Federal.</div>
</div>
</body></html>`;
}
